/**
 * disclosuresAdmin.service.ts — Admin > Forms/Templates > Disclosures (legacy
 * admin_disclosures_list.php / _details.php): a named disclosure made of text
 * blocks, printed on receipts, print templates and payment links, through
 * /disclosures. Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export interface AdminDisclosure {
  id: number;
  name: string;
  /** The text blocks, in order. */
  sections: string[];
  active: boolean;
  /** The one the payment link page offers (legacy has no control for it). */
  paymentLink: boolean;
}

export const DISCLOSURE_NAME_MAX = 255;

const flag = (v: unknown) => v === true || v === 1 || v === '1';

function toDisclosure(raw: Record<string, unknown>): AdminDisclosure {
  const sections = Array.isArray(raw.sections) ? (raw.sections as Record<string, unknown>[]) : [];
  return {
    id: Number(raw.disclosuresID) || 0,
    name: String(raw.name ?? ''),
    sections: sections.map((s) => String(s.text ?? '')),
    active: flag(raw.active),
    paymentLink: flag(raw.paymentlink),
  };
}

/** Every disclosure, deleted ones too, with its blocks. */
export function listDisclosures(): Promise<AdminDisclosure[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>('/disclosures')
    .then((r) => (r.data.data ?? []).map(toDisclosure));
}

/** Blank blocks are left out (legacy dropped them). */
function body(name: string, sections: string[]) {
  return { name: name.trim(), sections: sections.map((s) => s.trim()).filter((s) => s !== '') };
}

export function createDisclosure(name: string, sections: string[]): Promise<void> {
  return apiClient.post('/disclosures', body(name, sections)).then(() => undefined);
}

export function updateDisclosure(id: number, name: string, sections: string[]): Promise<void> {
  return apiClient.put(`/disclosures/${id}`, body(name, sections)).then(() => undefined);
}

export function deleteDisclosure(id: number): Promise<void> {
  return apiClient.delete(`/disclosures/${id}`).then(() => undefined);
}

export function restoreDisclosure(id: number): Promise<void> {
  return apiClient.post(`/disclosures/${id}/restore`).then(() => undefined);
}
